import { Dungeon } from '../dungeon/Dungeon';
import { Vec3 } from '../types';
import { Geometry } from '../renderer/geometry';
import { shuffle } from '../utils/random';

export interface WebTrap {
  position: Vec3;
  cellX: number;
  cellZ: number;
  triggered: boolean;
}

export const WEB_TRAP_TRIGGER_RADIUS = 0.4;
export const WEB_TRAP_SLOW_MULTIPLIER = 0.5;
export const WEB_TRAP_SLOW_DURATION = 3000;

function countWalkableNeighbors(dungeon: Dungeon, x: number, z: number): number {
  let count = 0;
  if (dungeon.isWalkable(x - 1, z)) count++;
  if (dungeon.isWalkable(x + 1, z)) count++;
  if (dungeon.isWalkable(x, z - 1)) count++;
  if (dungeon.isWalkable(x, z + 1)) count++;
  return count;
}

export function placeWebTrap(
  dungeon: Dungeon,
  excludeCell: { x: number; z: number },
  excludeRadius: number,
  occupiedCells: Set<string>
): WebTrap | null {
  const junctionCells: { x: number; z: number }[] = [];
  const fallbackCells: { x: number; z: number }[] = [];

  for (let z = 1; z < dungeon.height - 1; z++) {
    for (let x = 1; x < dungeon.width - 1; x++) {
      if (!dungeon.isWalkable(x, z)) continue;
      if (occupiedCells.has(`${x},${z}`)) continue;

      const dx = x - excludeCell.x;
      const dz = z - excludeCell.z;
      if (Math.sqrt(dx * dx + dz * dz) < excludeRadius) continue;

      const neighbors = countWalkableNeighbors(dungeon, x, z);
      if (neighbors >= 2) {
        junctionCells.push({ x, z });
      } else {
        fallbackCells.push({ x, z });
      }
    }
  }

  shuffle(junctionCells);
  shuffle(fallbackCells);
  const candidates = [...junctionCells, ...fallbackCells];
  if (candidates.length === 0) return null;

  const cell = candidates[0];
  const worldPos = dungeon.cellToWorld(cell.x, cell.z);

  return {
    position: { x: worldPos.x, y: 0, z: worldPos.z },
    cellX: cell.x,
    cellZ: cell.z,
    triggered: false
  };
}

export function generateWebTrapGeometry(): Geometry {
  const vertices: number[] = [];
  const normal = [0, 1, 0];
  const y = 0.005; // Slightly above floor to avoid z-fighting
  const spokeCount = 8;
  const spokeWidth = 0.008;
  const spokeRadius = 0.35;
  const ringRadii = [0.1, 0.2, 0.3];
  const ringWidth = 0.006;

  // Helper to push a quad (2 triangles) on the XZ plane
  function pushQuad(x0: number, z0: number, x1: number, z1: number, x2: number, z2: number, x3: number, z3: number) {
    // Triangle 1
    vertices.push(x0, y, z0, ...normal, 0, 0, 0);
    vertices.push(x1, y, z1, ...normal, 0, 0, 0);
    vertices.push(x2, y, z2, ...normal, 0, 0, 0);
    // Triangle 2
    vertices.push(x0, y, z0, ...normal, 0, 0, 0);
    vertices.push(x2, y, z2, ...normal, 0, 0, 0);
    vertices.push(x3, y, z3, ...normal, 0, 0, 0);
  }

  // Spokes: thin quads from center to edge
  for (let i = 0; i < spokeCount; i++) {
    const angle = (i / spokeCount) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dz = Math.sin(angle);
    // Perpendicular direction for width
    const px = -dz * spokeWidth;
    const pz = dx * spokeWidth;

    pushQuad(
      px, pz,           // near center, left
      -px, -pz,         // near center, right
      dx * spokeRadius - px, dz * spokeRadius - pz, // far, right
      dx * spokeRadius + px, dz * spokeRadius + pz   // far, left
    );
  }

  // Concentric rings: series of small quads around the circle
  const ringSegments = 32;
  for (const r of ringRadii) {
    for (let i = 0; i < ringSegments; i++) {
      const a0 = (i / ringSegments) * Math.PI * 2;
      const a1 = ((i + 1) / ringSegments) * Math.PI * 2;
      const rInner = r - ringWidth;
      const rOuter = r + ringWidth;

      pushQuad(
        Math.cos(a0) * rInner, Math.sin(a0) * rInner,
        Math.cos(a0) * rOuter, Math.sin(a0) * rOuter,
        Math.cos(a1) * rOuter, Math.sin(a1) * rOuter,
        Math.cos(a1) * rInner, Math.sin(a1) * rInner
      );
    }
  }

  return {
    vertices: new Float32Array(vertices),
    vertexCount: vertices.length / 9
  };
}
